import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Download } from "lucide-react";
import { LogoMark } from "./Logo";
import { InstallGuide } from "./InstallGuide";
import { canPrompt, isInAppBrowser, isIOS, isMobile, isStandalone, openInstall } from "@/lib/install";

const KEY = "jaypanduh_install_dismissed";

export const InstallPrompt = () => {
  const [ready, setReady] = useState(canPrompt());
  const [show, setShow] = useState(false);
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    const onHelp = () => { setShow(false); setGuide(true); };
    const onReady = () => setReady(true);
    window.addEventListener("install-help", onHelp);
    window.addEventListener("install-ready", onReady);
    let dismissed = false;
    try { dismissed = localStorage.getItem(KEY) === "1"; } catch { /* ignore */ }
    const auto = !dismissed && !isStandalone() && isMobile();
    if (auto && canPrompt()) setShow(true);
    const onAutoReady = () => { if (auto) setShow(true); };
    window.addEventListener("install-ready", onAutoReady);
    const t = auto && (isIOS() || isInAppBrowser()) ? setTimeout(() => setShow(true), 2500) : null;
    return () => {
      window.removeEventListener("install-help", onHelp);
      window.removeEventListener("install-ready", onReady);
      window.removeEventListener("install-ready", onAutoReady);
      if (t) clearTimeout(t);
    };
  }, []);

  const close = () => {
    setShow(false);
    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
  };
  const install = async () => {
    await openInstall();
    setReady(false);
    close();
  };

  return (
    <>
    <InstallGuide open={guide} onOpenChange={setGuide} />
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="install-prompt"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 160, damping: 20 }}
          className="fixed z-[70] left-3 right-3 md:left-auto md:w-[440px] bottom-[calc(12px+env(safe-area-inset-bottom))] bg-[#121216]/95 backdrop-blur-xl border border-[#56ED4A]/40 p-4 flex items-center gap-3"
        >
          <LogoMark className="w-12 h-12 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-display uppercase text-xl leading-none">Get the JayPanduh app</p>
            {isInAppBrowser() ? (
              <p data-testid="install-prompt-inapp-help" className="text-xs text-zinc-300 mt-1">Open this page in {isIOS() ? "Safari" : "Chrome"} to install it.</p>
            ) : (
              <p className="text-xs text-zinc-300 mt-1">Shows + crew links, right on your home screen.</p>
            )}
          </div>
          {ready ? (
            <button data-testid="install-prompt-install-btn" onClick={install} className="bg-[#56ED4A] text-black px-3 py-2 font-mono text-xs uppercase tracking-[0.15em] font-semibold flex items-center gap-1">
              <Download size={14} /> Install
            </button>
          ) : (
            <button data-testid="install-prompt-howto-btn" onClick={() => { setShow(false); setGuide(true); }} className="bg-[#56ED4A] text-black px-3 py-2 font-mono text-xs uppercase tracking-[0.15em] font-semibold whitespace-nowrap">
              Show me how
            </button>
          )}
          <button data-testid="install-prompt-close-btn" onClick={close} aria-label="Dismiss" className="p-1 text-zinc-400 hover:text-white"><X size={18} /></button>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};
